import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, MapPin, Phone } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { SERVICE_AREA_CITIES, PRIMARY_MARKETS } from "@/data/serviceAreaCities";
import { canonicalLink, canonicalUrl, geoMeta, SITE_URL } from "@/lib/seo";
import { businessHeadScripts } from "@/lib/businessJsonLd";

const TITLE = "Texas Service Areas — Awnings, Shade & Covers | Texas Made Shade";
const DESCRIPTION =
  "Every Texas city we serve from our Seguin fabrication shop — San Antonio, Austin, New Braunfels, Seguin, Boerne, San Marcos, the Hill Country, Corpus Christi and the Gulf Coast.";

export const Route = createFileRoute("/service-areas")({
  head: () => ({
    links: [canonicalLink("/service-areas")],
    meta: [
      ...geoMeta(),
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      {
        name: "keywords",
        content:
          "awning service areas texas, san antonio awnings, austin awnings, new braunfels shade, seguin awning shop, boerne shade, san marcos awnings, hill country shade, corpus christi marine canvas, gulf coast covers",
      },
      { property: "og:title", content: "Texas Service Areas — Texas Made Shade" },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: canonicalUrl("/service-areas") },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    scripts: [
      ...businessHeadScripts(),
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
            { "@type": "ListItem", position: 2, name: "Texas Service Areas", item: canonicalUrl("/service-areas") },
          ],
        }),
      },
    ],
  }),
  component: ServiceAreasPage,
});

function CityGrid({ cities }: { cities: typeof SERVICE_AREA_CITIES }) {
  return (
    <div className="grid gap-1.5 grid-cols-2 sm:gap-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
      {cities.map((c) => (
        <Link
          key={c.to}
          to={c.to}
          className="group flex items-center justify-between gap-1.5 border border-navy-deep/15 bg-white px-2.5 py-1.5 shadow-card transition hover:border-navy-deep hover:bg-navy-deep hover:text-white sm:px-3 sm:py-2"
        >
          <span className="min-w-0">
            <span className="block truncate font-display text-[13px] font-bold uppercase leading-tight text-navy-deep group-hover:text-white sm:text-sm">
              {c.city}
            </span>
            <span className="mt-0.5 flex items-center gap-1 text-[9px] uppercase tracking-wider text-navy-deep/60 group-hover:text-white/75 sm:text-[10px]">
              <MapPin className="h-2.5 w-2.5 text-red group-hover:text-white" />
              <span className="truncate">{c.region}</span>
            </span>
          </span>
          <ArrowRight className="hidden h-3.5 w-3.5 shrink-0 text-red opacity-0 transition group-hover:translate-x-0.5 group-hover:opacity-100 group-hover:text-white sm:block" />
        </Link>
      ))}
    </div>
  );
}

function ServiceAreasPage() {
  const primarySlugs = new Set(PRIMARY_MARKETS.map((c) => c.to));
  const rest = SERVICE_AREA_CITIES.filter((c) => !primarySlugs.has(c.to));

  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      <main id="main-content" tabIndex={-1} className="bg-cream">
        <section className="border-b-8 border-navy-deep bg-navy-deep py-14 text-white lg:py-20">
          <div className="mx-auto max-w-7xl px-4 lg:px-8">
            <p className="font-display text-xs uppercase tracking-[0.3em] text-red">
              Local Crews • One Texas Shop
            </p>
            <h1 className="mt-3 font-display text-4xl font-bold uppercase leading-tight md:text-5xl">
              Texas Service Areas
            </h1>
            <p className="mt-4 max-w-3xl text-base text-white/85 md:text-lg">
              We fabricate in Seguin and install with our own crews across San Antonio, Austin, the
              Hill Country, South Texas and the Gulf Coast. Pick your city for local projects,
              lead times and answers.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <a
                href="tel:+18303909588"
                className="inline-flex items-center gap-2 bg-red px-6 py-3.5 font-display text-base uppercase tracking-wider text-white shadow-bold transition hover:bg-red-deep"
              >
                <Phone className="h-5 w-5" /> +1 (830) 390-9588
              </a>
              <Link
                to="/estimate-quotes"
                className="inline-flex items-center gap-2 border-2 border-white px-6 py-3.5 font-display text-base uppercase tracking-wider text-white transition hover:bg-white hover:text-navy-deep"
              >
                Request a Quote <ArrowRight className="h-5 w-5" />
              </Link>
            </div>
          </div>
        </section>

        <section className="py-12 lg:py-16">
          <div className="mx-auto max-w-7xl px-4 lg:px-8">
            <div className="font-display text-xs uppercase tracking-[0.3em] text-red">★ Primary Markets</div>
            <h2 className="mt-2 font-display text-2xl font-bold uppercase text-navy-deep md:text-3xl">
              Where we work every week
            </h2>
            <div className="mt-6">
              <CityGrid cities={PRIMARY_MARKETS} />
            </div>

            <div className="mt-14 font-display text-xs uppercase tracking-[0.3em] text-red">★ All Texas Cities We Serve</div>
            <h2 className="mt-2 font-display text-2xl font-bold uppercase text-navy-deep md:text-3xl">
              Statewide coverage
            </h2>
            <p className="mt-2 max-w-3xl text-sm text-navy-deep/75 md:text-base">
              Central Texas, the Hill Country, South Texas, the Rio Grande Valley, the Gulf Coast and
              the Houston metro. Don&apos;t see your town? Call us — we travel for the right project.
            </p>
            <div className="mt-6">
              <CityGrid cities={rest} />
            </div>

            <div className="mt-12 flex flex-col items-center justify-between gap-4 border-t-4 border-navy-deep bg-white px-6 py-8 shadow-card sm:flex-row">
              <div className="text-center sm:text-left">
                <div className="font-display text-xs uppercase tracking-[0.3em] text-red">★ Questions About Your Area?</div>
                <div className="mt-1 font-display text-xl font-bold uppercase text-navy-deep md:text-2xl">
                  Travel, lead times and measures — answered.
                </div>
              </div>
              <Link
                to="/local-faqs"
                className="inline-flex items-center justify-center gap-2 bg-red px-6 py-3 font-display text-xs uppercase tracking-wider text-white shadow-bold transition hover:bg-red-deep"
              >
                Service Area FAQs <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
